import { useApi, useCookie, useCurrentLocation, useCurrentUserProfile, useNuxtApp, useState } from "#imports";
import { PUBLIC_ROUTE_NAMES, ROUTE_LOGIN, ROUTE_ONBOARDING, ROUTE_SWITCHER } from "~/constants/routes";
import { QUERY_ONBOARDING_STATUS } from "~/constants/queryKeys";
import { getOAuthUser } from "~/utils/oauthUserStorage";
import type { LocationIdentifier } from "~/types/api";
import { isContextFreeEmployeeAdminPage } from "~/utils/globalAdmin";

const onboardingConfirmedLocationId = () =>
  useState<LocationIdentifier | null>("onboarding-confirmed-location-id", () => null);

export default defineNuxtRouteMiddleware(async (to) => {
  if (PUBLIC_ROUTE_NAMES.includes(String(to.name))) {
    return;
  }

  if ([ROUTE_SWITCHER, ROUTE_LOGIN, "/logout", ROUTE_ONBOARDING].includes(to.path)) {
    return;
  }

  const user = getOAuthUser();
  if (isContextFreeEmployeeAdminPage(to.path, user)) return;
  if (!user) {
    return;
  }

  const { currentLocationId } = useCurrentLocation();

  const confirmedLocationId = onboardingConfirmedLocationId();
  if (confirmedLocationId.value !== null && confirmedLocationId.value === currentLocationId.value) {
    return;
  }

  const { currentUserProfile, isEmployee, isAlphaUser } = await useCurrentUserProfile();

  // TODO: remove this gate once onboarding is ready to roll out beyond alpha testers.
  if (!currentLocationId.value || !currentUserProfile || !isEmployee || !isAlphaUser) {
    return;
  }

  const { $queryClient } = useNuxtApp();

  try {
    const { data: status } = await $queryClient.fetchQuery({
      queryKey: [QUERY_ONBOARDING_STATUS, currentLocationId.value],
      queryFn: async () => {
        const api = await useApi();
        return api.getOnboardingStatus();
      },
      staleTime: 30 * 1000,
    });

    if (!status.is_confirmed) {
      const guidedRoutes = status.steps
        .flatMap((step) => step.fields)
        .filter((field) => field.fulfillment.type === "guided" && field.fulfillment.route)
        .map((field) => field.fulfillment.route as string);

      if (guidedRoutes.includes(to.path)) {
        return;
      }

      const onboardingRedirectCookie = useCookie("onboarding_redirect_uri");
      onboardingRedirectCookie.value = to.fullPath;

      return navigateTo(ROUTE_ONBOARDING);
    }

    confirmedLocationId.value = currentLocationId.value;
  } catch {
    // The onboarding status couldn't be resolved for this profile/location - don't block
    // navigation, since RequiresProfileForCurrentLocation/IsEmployeePolicy already guard
    // the endpoint itself on the backend.
  }
});
